import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MdArrowBack, MdFileDownload } from 'react-icons/md';
import deltaService from '../../services/deltaService';
import { useAuth } from '../../context/AuthContext';
import PolicyUpload from './PolicyUpload';
import ReviewTrail from './ReviewTrail';
import { Chip, Empty } from './DeltaUI';
import { toneVars } from './DeckUI';

// One policy, read on screen, beside the signed document it was extracted from.
//
// THE PROVENANCE BAR IS NOT DECORATION. This screen shows a RENDERING of a signed
// document, and the two can only be trusted together. So the file's name, version and
// upload date sit above the text with a download link, permanently and un-dismissably. A
// reader must never be able to quote this page without knowing which signed document it
// came from — that is the entire safety argument for rendering policy text at all.
//
// There is no edit control, here or anywhere: sections exist only as the output of
// extracting a document, and the API has no write path for them. The only way to change
// this text is to re-upload the document, which is what keeps the two from diverging.

const STATUS_CHIP = {
  in_force: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  drafted: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  drafting: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  not_started: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  withdrawn: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
};

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  : '');

const fmtSize = (bytes) => (bytes
  ? `${(bytes / 1024 / 1024).toFixed(bytes < 1024 * 1024 ? 2 : 1)} MB`
  : '');

export default function PolicyDocumentPage() {
  const { reference } = useParams();
  const { isAdmin, canWrite } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    deltaService.getPolicyReading(reference)
      .then(setData)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [reference]);

  useEffect(() => { load(); }, [load]);

  const download = useCallback(async () => {
    if (!data?.source?.id) return;
    try {
      const r = await deltaService.downloadDocument(data.source.id);
      // The API answers with a short-lived presigned URL rather than the bytes, so the
      // file never passes through the app box.
      const url = r?.url || r?.download_url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* the button simply does nothing; the vault refuses rather than falling back */
    }
  }, [data]);

  const policy = data?.policy;
  const shownVersion = data?.version;
  const isDraftView = shownVersion && !shownVersion.in_force;
  const sections = data?.sections || [];
  const source = data?.source;

  if (loading) return <Empty>Loading…</Empty>;
  if (failed || !policy) return <Empty>That policy is not on the register.</Empty>;

  return (
    <div>
      {/* Its own trail back, now that this page is no longer nested inside DeltaSection.
          One level up is the workspace it was opened from. */}
      <div className="mb-4 flex items-center gap-2 text-[11px]">
        <Link to="/policies"
              className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
          <MdArrowBack size={14} />
          Policy Pack
        </Link>
      </div>

      <header className="mb-4">
        <div className="text-[9px] uppercase tracking-[2.5px] text-gray-500 dark:text-gray-400">
          {policy.reference} · {policy.level_display || `Level ${policy.level}`}
        </div>
        <h1 className="mt-1.5 font-serif text-[24px] leading-tight tracking-tight text-gray-900 dark:text-gray-50 sm:text-[30px]">
          {policy.title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Chip className={STATUS_CHIP[policy.status]}>{policy.status_display}</Chip>
          {policy.tier ? <Chip className="bg-gray-500/10 text-gray-500 dark:text-gray-400">Tier {policy.tier}</Chip> : null}
          <Chip className="bg-gray-500/10 text-gray-500 dark:text-gray-400">
            Approved by {policy.approval_authority === 'shareholder' ? 'the Shareholder' : 'the CIO'}
          </Chip>
        </div>
      </header>

      {canWrite && <PolicyUpload reference={reference} onPublished={load} isAdmin={isAdmin} />}

      {isDraftView && (
        <section className="mb-4 rounded-lg border-l-2 border-amber-500 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 sm:px-4">
          <strong>This is a draft, not the approved policy.</strong> Version {shownVersion.label} is
          at &ldquo;{shownVersion.stage_display}&rdquo;. Nothing is in force for this policy yet, so
          it is shown here for the people working on it.
        </section>
      )}

      {source ? (
        <section className="mb-6 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117] sm:p-4"
                 style={toneVars('navy')}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">
                {isDraftView ? 'Draft document — not yet approved' : 'Signed document — the authority'}
              </div>
              <div className="mt-0.5 truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                {source.original_filename || source.title}
              </div>
              <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                Version {source.version} · uploaded {fmtDate(source.uploaded_at)}
                {source.size ? ` · ${fmtSize(source.size)}` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={download}
              className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-[12px] font-medium text-gray-700 transition-colors hover:border-brand-gold hover:text-brand-gold dark:border-gray-700 dark:text-gray-200"
            >
              <MdFileDownload size={16} />
              Download
            </button>
          </div>
          {sections.length > 0 && (
            <p className="mt-2.5 border-t border-gray-100 pt-2.5 text-[11px] leading-relaxed text-gray-500 dark:border-gray-800 dark:text-gray-400">
              {isDraftView
                ? 'The text below is a rendering of that draft. Nothing here is in force.'
                : 'The text below is a rendering of that file, for reading and citation. '
                  + 'Where the two differ, the signed document governs.'}
            </p>
          )}
        </section>
      ) : (
        <section className="mb-6 rounded-lg border-l-2 border-amber-500 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 sm:px-4">
          No document has been attached to this policy yet, so there is nothing to read or
          download. Its status above is the only thing recorded.
        </section>
      )}

      {sections.length === 0 && source && (
        <Empty>
          The attached file has no on-screen rendering — download it above to read it.
        </Empty>
      )}

      <article className="space-y-5">
        {sections.map((s) => <Section key={s.order} section={s} />)}
      </article>

      <ReviewTrail version={data?.version} versions={data?.versions} onChanged={load} />
    </div>
  );
}

// Depth drives size, not indentation: indenting six levels on a 375px screen leaves a
// column four words wide. Nesting is shown by weight and a rule on the top two levels.
function Section({ section }) {
  const { depth, number, heading, anchor, blocks } = section;
  const size = depth === 1 ? 'text-[17px] sm:text-[19px]'
    : depth === 2 ? 'text-[15px]'
      : 'text-[13.5px]';

  return (
    <section id={anchor} className="scroll-mt-20">
      {(heading || number) && (
        <h2 className={`font-serif leading-tight text-gray-900 dark:text-gray-50 ${size} ${
          depth === 1 ? 'border-b border-gray-200 pb-1.5 dark:border-gray-800' : ''}`}
            style={toneVars('navy')}>
          {number && (
            <span className="tone-text mr-2 font-sans text-[0.8em] font-semibold tabular-nums">
              {number}
            </span>
          )}
          {heading}
        </h2>
      )}
      <div className="mt-2 space-y-2.5">
        {blocks.map((b, i) => (b.kind === 'table'
          ? <Table key={i} rows={b.rows} />
          : (
            <p key={i} className="text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">
              {b.text}
            </p>
          )))}
      </div>
    </section>
  );
}

// Scrolls inside its own container — the house rule. A schedule of twenty-three policies is
// wider than a phone and must never make the page itself scroll sideways.
function Table({ rows }) {
  const [head, ...body] = rows;
  return (
    <div className="scrollbar-thin -mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[420px] text-[12px]">
        <thead>
          <tr className="border-b border-gray-200 text-left text-[9px] uppercase tracking-[1.5px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
            {head.map((c, i) => <th key={i} className="py-1.5 pr-3 font-normal">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r} className="border-b border-gray-100 dark:border-gray-800">
              {row.map((c, i) => (
                <td key={i} className="py-1.5 pr-3 align-top text-gray-700 dark:text-gray-300">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
